/**
 * Currency-aware amount formatting for display.
 *
 * The API deliberately serves exact decimal strings at the store's full scale
 * (e.g. "5050.9100"); formatting is a display concern. Intl resolves the
 * correct minor-unit count per currency (2 for EUR/USD, 3 for e.g. BHD) and
 * adds thousands separators. `signed` renders an explicit "+" on positive
 * amounts (incoming transactions); balances stay unsigned.
 *
 * Output keeps the app's "<number> <CODE>" layout, so the currency part Intl
 * produces is stripped and the code re-appended.
 */
export function formatAmount(amount: string, currency: string, opts?: { signed?: boolean }): string {
  const value = Number(amount);
  if (!Number.isFinite(value)) return `${amount} ${currency}`;
  try {
    const parts = new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      currencyDisplay: 'code',
      signDisplay: opts?.signed ? 'exceptZero' : 'auto',
    }).formatToParts(value);
    const num = parts
      .filter((p) => p.type !== 'currency')
      .map((p) => p.value)
      .join('')
      .trim();
    return `${num} ${currency}`;
  } catch {
    // Unknown/non-ISO currency code: fall back to the raw API string.
    return `${amount} ${currency}`;
  }
}
